import { useTranslation } from 'react-i18next';

import type { IconType } from '@kognitos/lattice';

import { ChatActivity } from '@/shared/components/ChatActivity';

import type {
  IRainToolCallState,
  IRainToolActivityLabel,
} from '../hooks/types';
import type { TRainToolCallStatus } from './RainToolCallCard';

export interface IRainToolActivityProps {
  /** The tool the row is for. Not drawn; read by an agent's own work block. */
  toolName?: string;
  /** Original call for agent-specific grouping; never rendered as a payload. */
  call?: IRainToolCallState;
  status: TRainToolCallStatus;
  /** The row's copy. `undefined` renders the icon alone (see the resolver). */
  label?: IRainToolActivityLabel;
  /** Leading icon; the error state overrides it. */
  icon?: IconType;
  className?: string;
  /** Emphasizes activity in business-facing conversations. */
  prominent?: boolean;
}

/**
 * One tool call as a copy-only activity row (see
 * `IRainAgentConfig.toolActivities`). Status reads as it does on the generic
 * card: shimmer while running, muted once done, the error treatment on failure
 * — so a completed row goes quiet rather than stacking up confirmation ticks.
 */
export function RainToolActivity({
  status,
  label,
  icon = 'Wrench',
  className,
  prominent = false,
}: IRainToolActivityProps) {
  const { t } = useTranslation();
  const isError = status === 'error';

  return (
    <ChatActivity
      variant="tool-call"
      title={label ? t(label.key, { ns: label.ns, ...label.values }) : ''}
      isActive={status === 'running'}
      compact={!prominent}
      prominent={prominent}
      isError={isError}
      icon={
        prominent
          ? isError
            ? 'CircleX'
            : status === 'success'
              ? 'CircleCheck'
              : 'Loader2'
          : isError
            ? 'CircleAlert'
            : icon
      }
      className={className}
      data-testid="rain-tool-activity"
      data-status={status}
    />
  );
}
